import { useMemo, useState } from 'react';
import OddsCell from './OddsCell';
import { formatPrice } from '../../utils/oddsFormat';
import { getBookmakerColumnsFromEvent } from '../../utils/bookmakers';

export default function GolfTable({ tournament, oddsFormat }) {
  const [selected, setSelected] = useState(null);
  const event = tournament.events[0];
  if (!event) return null;

  const columns = useMemo(() => getBookmakerColumnsFromEvent(event), [event]);

  const golferData = useMemo(() => {
    const map = {};
    for (const bm of event.bookmakers || []) {
      const mkt = bm.markets?.find((m) => m.key === 'outrights');
      if (!mkt) continue;
      for (const o of mkt.outcomes) { if (!map[o.name]) map[o.name] = {}; map[o.name][bm.key] = o.price; }
    }
    return map;
  }, [event]);

  const sorted = useMemo(() => {
    const entries = Object.entries(golferData).map(([name, prices]) => {
      const vals = Object.values(prices).sort((a, b) => a - b);
      return { name, prices, consensus: vals.length ? vals[Math.floor(vals.length / 2)] : Infinity };
    });
    entries.sort((a, b) => a.consensus - b.consensus);
    return entries;
  }, [golferData]);

  const bestMap = useMemo(() => {
    const m = {};
    for (const { name, prices } of sorted) {
      let bk = null, bp = Infinity;
      for (const [k, p] of Object.entries(prices)) { if (p < bp) { bp = p; bk = k; } }
      m[name] = bk;
    }
    return m;
  }, [sorted]);

  return (
    <div>
      <div className="mb-3">
        <h2 className="text-sm font-bold text-[#1e293b] dark:text-[#e2e8f0]">{tournament.sport.title}</h2>
        <p className="text-[11px] text-[#8892a8]">{sorted.length} golfers &middot; Outrights</p>
      </div>
      <div className="overflow-x-auto rounded border border-[#e2e8f0] dark:border-[#1e2536]">
        <table className="w-full text-[13px]" style={{ fontFamily: 'var(--font-mono)' }}>
          <thead>
            <tr className="bg-[#f1f5f9] dark:bg-[#0a0d14] text-[#64748b] dark:text-[#505a6e]">
              <th className="text-left px-2 py-2 text-[10px] uppercase tracking-[0.05em] font-semibold w-8" style={{ fontFamily: 'var(--font-sans)' }}>#</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.05em] font-semibold min-w-[160px]" style={{ fontFamily: 'var(--font-sans)' }}>Golfer</th>
              {columns.map((c) => <th key={c.key} className="text-center px-2 py-2 text-[10px] uppercase tracking-[0.05em] font-semibold min-w-[100px] border-l border-[#e2e8f0] dark:border-[#1e2536]" style={{ fontFamily: 'var(--font-sans)' }}>{c.title}</th>)}
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ name, prices }, i) => {
              const isSel = selected === name;
              const even = i % 2 === 0;
              return (
                <tr key={name} onClick={() => setSelected(isSel ? null : name)}
                  className={`cursor-pointer transition-colors duration-150 ${isSel ? 'bg-[#eff6ff] dark:bg-[rgba(59,130,246,0.08)]' : even ? 'bg-white dark:bg-[#151a25]' : 'bg-[#f8fafc] dark:bg-[#1a2030]'} hover:bg-[#eff6ff] dark:hover:bg-[#1e2a40]`}>
                  <td className="px-2 py-2 text-[#94a3b8] dark:text-[#505a6e] text-[11px]">{i + 1}</td>
                  <td className="px-3 py-2 text-[12px] font-medium text-[#1e293b] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{name}</td>
                  {columns.map((c) => {
                    const p = prices[c.key];
                    if (p == null) return <td key={c.key} className="text-center px-2 py-2 text-[#cbd5e1] dark:text-[#2a3348] border-l border-[#f1f5f9] dark:border-[#1e2536]">—</td>;
                    return <td key={c.key} className="text-center px-2 py-2 border-l border-[#f1f5f9] dark:border-[#1e2536]"><OddsCell text={formatPrice(p, oddsFormat)} highlight={bestMap[name] === c.key ? 'best' : null} /></td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
